// DID TrustedDID admin client — built directly against BaseTrustedDIDController
// ([Route("api/v1/did/trusted-dids")], confirmed by reading the controller directly). Same
// conventions as identityKeyAdminApiClient.ts / TeamAdminApiClient.

import { BaseAdminApiClient, type AdminApiConfig, type AdminApiResult } from './baseAdminApiClient';

export type TrustDirection = 'Caller' | 'Sender';
export type TrustLevel = 'full' | 'partial' | 'conditional';

export interface TrustedDIDDTO {
  TrustedDIDID: number;
  NodeID: number;
  DID: string;
  DisplayName?: string | null;
  TrustLevel: TrustLevel;
  Direction: TrustDirection;
  VerifiedOn?: string | null;
  ExpiresAt?: string | null;
  Notes?: string | null;
  TenantID: number;
}

export interface TrustDIDInput {
  NodeID: number;
  DID: string;
  DisplayName?: string;
  TrustLevel: TrustLevel;
  Direction: TrustDirection;
  ExpiresAt?: string;
  Notes?: string;
}

export class TrustedDidAdminApiClient extends BaseAdminApiClient {
  constructor(config: AdminApiConfig) {
    super(config);
  }

  async getByNodeId(nodeId: number): Promise<AdminApiResult<TrustedDIDDTO[]>> {
    const result = await this.postQuery<TrustedDIDDTO[]>('/api/v1/did/trusted-dids/by-node', {
      NodeID: { ID: nodeId },
      PageInfo: { PageNumber: 1, PageSize: 200 },
    });
    return { ...result, data: Array.isArray(result.data) ? result.data : [] };
  }

  async trust(input: TrustDIDInput): Promise<AdminApiResult<{ id?: { id?: number } }>> {
    return this.postQuery<{ id?: { id?: number } }>('/api/v1/did/trusted-dids/trust', input);
  }

  async verify(trustedDidId: number): Promise<AdminApiResult<void>> {
    return this.postQuery<void>(`/api/v1/did/trusted-dids/${trustedDidId}/verify`, {
      TrustedDIDID: { ID: trustedDidId },
    });
  }

  async updateTrustLevel(trustedDidId: number, trustLevel: TrustLevel): Promise<AdminApiResult<void>> {
    return this.postQuery<void>(`/api/v1/did/trusted-dids/${trustedDidId}/trust-level`, {
      TrustedDIDID: { ID: trustedDidId },
      TrustLevel: trustLevel,
    });
  }

  async revoke(trustedDidId: number, reason?: string): Promise<AdminApiResult<void>> {
    return this.postQuery<void>(`/api/v1/did/trusted-dids/${trustedDidId}/revoke`, {
      TrustedDIDID: { ID: trustedDidId },
      Reason: reason ?? '',
    });
  }
}
